import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useI18n } from "@/context/I18nContext";
import { Sparkles } from "lucide-react";

/**
 * Dynamic scroll-driven menu:
 * - Sticky left column with categories (active = current section in view).
 * - Right column: category-wise items with staggered fade-in.
 * - Hover reveals dish preview image on the right side (desktop).
 * - Mobile: horizontal category pills at top.
 */
export default function MenuSection({ categories = [], items = [] }) {
    const { t, lang, localized } = useI18n();
    const [activeCat, setActiveCat] = useState(categories[0]?.slug || "");
    const [hoverDish, setHoverDish] = useState(null);
    const sectionRefs = useRef({});

    const itemsByCat = useMemo(() => {
        const grouped = {};
        for (const c of categories) grouped[c.slug] = [];
        for (const it of items) {
            if (!grouped[it.category]) grouped[it.category] = [];
            grouped[it.category].push(it);
        }
        return grouped;
    }, [categories, items]);

    // Track visible section using IntersectionObserver
    useEffect(() => {
        if (!categories.length) return;
        const observers = [];
        categories.forEach((c) => {
            const el = sectionRefs.current[c.slug];
            if (!el) return;
            const io = new IntersectionObserver(
                (entries) => {
                    entries.forEach((e) => {
                        if (e.isIntersecting && e.intersectionRatio > 0.35) {
                            setActiveCat(c.slug);
                        }
                    });
                },
                { rootMargin: "-30% 0px -50% 0px", threshold: [0.35, 0.6] }
            );
            io.observe(el);
            observers.push(io);
        });
        return () => observers.forEach((o) => o.disconnect());
    }, [categories]);

    const handleJump = (slug) => {
        const el = sectionRefs.current[slug];
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.scrollY - 110;
        window.scrollTo({ top: y, behavior: "smooth" });
    };

    return (
        <section id="menu" data-testid="menu-section" className="relative py-24 md:py-32 bg-habana-ink">
            {/* Background decorative gradients */}
            <div className="absolute inset-0 pointer-events-none opacity-70">
                <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-gold/10 blur-3xl" />
                <div className="absolute bottom-10 right-0 w-[420px] h-[420px] rounded-full bg-habana-red/15 blur-3xl" />
            </div>

            <div className="relative mx-auto max-w-7xl px-6 md:px-10">
                <div className="text-center max-w-2xl mx-auto mb-16">
                    <div className="ornament">{t("menu_ornament")}</div>
                    <h2
                        className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight text-habana-cream"
                        data-testid="menu-title"
                    >
                        {t("menu_title")}
                    </h2>
                    <p className="mt-4 text-habana-cream/60 leading-relaxed">{t("menu_sub")}</p>
                </div>

                {/* Mobile category pills */}
                <div className="lg:hidden -mx-6 px-6 mb-8 overflow-x-auto no-scrollbar">
                    <div className="flex gap-2 min-w-max">
                        {categories.map((c) => (
                            <button
                                key={c.slug}
                                onClick={() => handleJump(c.slug)}
                                data-testid={`menu-cat-pill-${c.slug}`}
                                className={`px-4 py-2 rounded-full text-sm font-serif tracking-wide border transition-colors ${
                                    activeCat === c.slug
                                        ? "bg-gold text-black border-gold"
                                        : "text-habana-cream/70 border-gold/25 hover:text-gold"
                                }`}
                            >
                                {localized(c, "name")}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Sticky categories (desktop) */}
                    <aside className="hidden lg:block lg:col-span-3">
                        <div className="sticky top-32 space-y-1 border-l border-gold/15 pl-4">
                            <div className="text-[0.7rem] tracking-[0.32em] uppercase text-gold/70 mb-4">
                                {t("menu_categories_label")}
                            </div>
                            {categories.map((c) => (
                                <button
                                    key={c.slug}
                                    onClick={() => handleJump(c.slug)}
                                    data-testid={`menu-cat-link-${c.slug}`}
                                    className={`menu-cat-link ${activeCat === c.slug ? "active" : ""}`}
                                >
                                    {localized(c, "name")}
                                </button>
                            ))}
                            <div className="pt-6 mt-6 border-t border-gold/10">
                                <div className="font-script text-gold text-xl">{t("menu_footer_tag")}</div>
                                <div className="text-xs text-habana-cream/50 mt-1">
                                    {t("menu_prices_note")}
                                </div>
                            </div>
                        </div>
                    </aside>

                    {/* Menu list */}
                    <div className="lg:col-span-6">
                        {categories.map((c, ci) => {
                            const catItems = itemsByCat[c.slug] || [];
                            return (
                                <div
                                    key={c.slug}
                                    ref={(el) => (sectionRefs.current[c.slug] = el)}
                                    data-testid={`menu-cat-section-${c.slug}`}
                                    className="mb-16 scroll-mt-32"
                                >
                                    <div className="flex items-baseline gap-4 mb-6">
                                        <span className="font-serif italic text-gold/70 text-xl">
                                            {String(ci + 1).padStart(2, "0")}.
                                        </span>
                                        <h3
                                            className="font-serif text-3xl md:text-4xl text-habana-cream"
                                            data-testid={`menu-cat-heading-${c.slug}`}
                                        >
                                            {localized(c, "name")}
                                        </h3>
                                    </div>
                                    <div className="space-y-0">
                                        {catItems.map((it, idx) => (
                                            <motion.div
                                                key={it.id}
                                                initial={{ opacity: 0, y: 24 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true, margin: "-50px" }}
                                                transition={{
                                                    duration: 0.7,
                                                    delay: 0.05 * idx,
                                                    ease: [0.22, 1, 0.36, 1],
                                                }}
                                                onMouseEnter={() =>
                                                    it.image_url && setHoverDish(it)
                                                }
                                                onMouseLeave={() => setHoverDish(null)}
                                                className="menu-row group"
                                                data-testid={`menu-item-${it.id}`}
                                            >
                                                <div className="flex items-baseline">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <h4 className="font-serif text-xl md:text-2xl text-habana-cream group-hover:text-gold transition-colors">
                                                                {localized(it, "name")}
                                                            </h4>
                                                            {it.featured && (
                                                                <span className="inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-widest text-habana-red">
                                                                    <Sparkles size={12} /> {t("menu_featured")}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="mt-1.5 text-sm md:text-[0.95rem] text-habana-cream/60 font-light max-w-xl">
                                                            {localized(it, "description")}
                                                        </p>
                                                    </div>
                                                    <div className="price-dots hidden sm:block" />
                                                    <div className="font-serif text-xl text-gold whitespace-nowrap">
                                                        {it.price}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                        {catItems.length === 0 && (
                                            <div className="text-habana-cream/40 italic text-sm py-4">
                                                {t("menu_soon")}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Hover preview */}
                    <aside className="hidden lg:block lg:col-span-3">
                        <div className="sticky top-32 h-[420px] rounded-2xl overflow-hidden border border-gold/15 bg-black/30">
                            <AnimatePresence mode="wait">
                                {hoverDish?.image_url ? (
                                    <motion.div
                                        key={hoverDish.id}
                                        initial={{ opacity: 0, scale: 1.02 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.35 }}
                                        className="relative w-full h-full"
                                    >
                                        <img
                                            src={hoverDish.image_url}
                                            alt={localized(hoverDish, "name")}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                                        <div className="absolute bottom-0 p-5">
                                            <div className="font-script text-gold text-xl">
                                                {t("menu_featured")}
                                            </div>
                                            <div className="font-serif text-2xl text-habana-cream">
                                                {localized(hoverDish, "name")}
                                            </div>
                                            <div className="mt-1 text-sm text-habana-cream/70">
                                                {localized(hoverDish, "description")}
                                            </div>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="empty"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="w-full h-full flex flex-col items-center justify-center text-center p-8 text-habana-cream/60"
                                    >
                                        <div className="text-6xl font-serif italic text-gold/40 mb-3">
                                            {t("menu_preview_title")}
                                        </div>
                                        <p className="font-script text-2xl text-gold">
                                            {t("menu_preview_hint")}
                                        </p>
                                        <p className="text-xs tracking-widest uppercase mt-2 text-habana-cream/40">
                                            {t("menu_preview_sub")}
                                        </p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </aside>
                </div>
            </div>
        </section>
    );
}
